/** Angular Imports */
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';

/** Third Party Imports */
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { AuthUser } from './auth.interface';

/**
 * Authentication service for managing user authentication state.
 *
 * @remarks
 * Provides centralized authentication state management using Angular signals.
 * Used by route guards to determine access control.
 *
 * Security Model:
 * - httpOnly cookie (sessionId) is used for secure API authentication
 * - sessionStorage (ntv_user_traits) stores user info for client-side display
 * - Auth status is determined by presence of user traits synced from backend
 */
@Injectable({
    providedIn: 'root',
})
export class AuthService {
    /**
     * Platform ID to check if running in browser
     * @type {Object}
     */
    private readonly platformId = inject(PLATFORM_ID);

    /**
     * HTTP client for backend communication
     * @type {HttpClient}
     */
    private readonly http = inject(HttpClient);

    /**
     * Auth0 service for browser-based login flow
     * @type {Auth0Service | null}
     */
    private readonly auth0 = isPlatformBrowser(this.platformId)
        ? inject(Auth0Service, { optional: true })
        : null;

    /**
     * Router instance for navigation
     */
    private readonly router = inject(Router);

    /**
     * Internal signal raised when another tab logs out. The app root binds
     * a `<app-confirmation-modal>` to this so the user sees a notice before
     * being redirected, instead of being yanked to `/login` mid-task.
     */
    private readonly _showLogoutNoticeModal = signal<boolean>(false);

    /**
     * Read-only signal indicating whether the cross-tab logout notice modal
     * should be shown. Consumed by the app root template.
     */
    public readonly showLogoutNoticeModal = this._showLogoutNoticeModal.asReadonly();

    /**
     * BroadcastChannel used to signal logout across tabs. More reliable than the
     * `storage` event because it always fires regardless of localStorage state and
     * doesn't depend on a value actually changing.
     */
    private _logoutChannel: BroadcastChannel | null = null;

    /**
     * Wires up cross-tab session synchronization in the browser.
     *
     * @remarks
     * The `storage` event fires in every other tab/window of the same origin
     * whenever `localStorage` is mutated — but **never** in the tab that made
     * the change. We use this to propagate logout across tabs:
     *
     * - When one tab calls `clearAuthentication()`, every other tab observes
     *   `ntv_user_traits` being removed and raises `_showLogoutNoticeModal`.
     *   The app root renders a confirmation modal informing the user that
     *   their session ended elsewhere; redirect to `/login` happens only
     *   after they acknowledge it via {@link dismissLogoutNotice}.
     *
     * - The `!event.newValue` guard catches both explicit `removeItem` and
     *   `setItem('ntv_user_traits', '')`. Updates that replace the value
     *   (e.g. token refresh) intentionally do NOT trigger logout.
     *
     * - Listener is only attached in the browser. SSR has no `window` and
     *   no concept of tabs, so this is a no-op on the server.
     */
    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            window.addEventListener('storage', (event) => {
                if (event.key === 'ntv_user_traits' && !event.newValue) {
                    this._showLogoutNoticeModal.set(true);
                }
            });

            if (typeof BroadcastChannel !== 'undefined') {
                this._logoutChannel = new BroadcastChannel('ntv-auth');
                this._logoutChannel.addEventListener('message', (event) => {
                    if (event.origin === window.location.origin && event.data?.type === 'logout') {
                        this._showLogoutNoticeModal.set(true);
                    }
                });
            }
        }
    }

    /**
     * Broadcasts a logout signal to all other same-origin tabs/windows.
     *
     * @returns {void}
     *
     * @remarks
     * Sender does not receive its own message — only other tabs do. Used by the
     * login page on mount: any other tab still authenticated will surface the
     * cross-tab logout notice modal so the user is informed before redirect.
     */
    public broadcastLogout(): void {
        this._logoutChannel?.postMessage({ type: 'logout' });
    }

    /**
     * Raises the logout notice modal in the current tab and broadcasts the same
     * signal to other tabs.
     *
     * @returns {void}
     *
     * @remarks
     * Bound to the HTTP interceptor's 401/403 path so an expired session surfaces
     * the same `<app-confirmation-modal>` used for cross-tab logout, instead of
     * a hard redirect to `/login`. Caller is responsible for any server-side
     * cookie cleanup prior to invocation; redirect happens on user dismissal via
     * {@link dismissLogoutNotice}.
     */
    public notifySessionExpired(): void {
        this._showLogoutNoticeModal.set(true);
        this.broadcastLogout();
    }

    /**
     * Dismisses the cross-tab logout notice modal, clears any lingering local
     * auth state, and redirects to `/login`.
     *
     * @returns {void}
     *
     * @remarks
     * Bound to the modal's `confirm` and `modalClose` outputs in the app root.
     * Safe to call multiple times — clearing already-cleared state is a no-op.
     */
    public dismissLogoutNotice(): void {
        this._showLogoutNoticeModal.set(false);
        this.clearAuthentication();
        this.router.navigate(['/login']);
    }

    /**
     * Signal indicating whether the user is authenticated
     * @type {WritableSignal<boolean>}
     */
    private readonly _isAuthenticated = signal<boolean>(false);

    /**
     * Read-only computed signal for authentication state
     * @type {Signal<boolean>}
     */
    public readonly isAuthenticated = this._isAuthenticated.asReadonly();

    /**
     * Reactive signal for user traits, kept in sync with sessionStorage/localStorage
     * @type {WritableSignal<unknown>}
     */
    private readonly _userTraits = signal<unknown>(this.readStoredTraits());

    /**
     * Read-only signal exposing current user traits to consumers
     * @type {Signal<unknown>}
     */
    public readonly userTraits = this._userTraits.asReadonly();

    /**
     * Reads user traits directly from sessionStorage/localStorage during init.
     */
    private readStoredTraits(): unknown {
        if (!isPlatformBrowser(this.platformId)) return null;
        const data =
            sessionStorage.getItem('ntv_user_traits') ?? localStorage.getItem('ntv_user_traits');
        if (!data) return null;
        try {
            return JSON.parse(data);
        } catch {
            return null;
        }
    }

    /**
     * Signal holding the current user traits/info
     * @type {WritableSignal<AuthUser | null>}
     */
    private readonly _user = signal<AuthUser | null>(null);

    /**
     * Read-only computed signal for user traits
     * @type {Signal<AuthUser | null>}
     */
    public readonly user = this._user.asReadonly();

    /**
     * Initiates the Auth0 login flow.
     * Only executes in the browser environment.
     *
     * @returns {void}
     */
    public login(): void {
        if (isPlatformBrowser(this.platformId) && this.auth0) {
            this.auth0
                .loginWithRedirect({
                    authorizationParams: {
                        prompt: 'login', // forces Auth0 to show the login form
                    },
                })
                .subscribe();
        } else {
            console.warn('Auth0 service is not available or running outside browser context.');
        }
    }
    /**
     * Checks if user has a valid authentication session.
     *
     * @returns {boolean} True if user is authenticated
     *
     * @remarks
     * Checks for presence of user traits in sessionStorage.
     * User traits are synced from backend after successful auth validation.
     */
    public checkAuthStatus(): boolean {
        if (!isPlatformBrowser(this.platformId)) {
            return false;
        }
        // sessionStorage is tab-scoped — fall back to localStorage so new tabs don't lose auth.
        const userTraits =
            sessionStorage.getItem('ntv_user_traits') ?? localStorage.getItem('ntv_user_traits');
        if (userTraits && !sessionStorage.getItem('ntv_user_traits')) {
            sessionStorage.setItem('ntv_user_traits', userTraits);
        }
        const isAuth = !!userTraits;

        if (isAuth && userTraits) {
            try {
                this._user.set(JSON.parse(userTraits) as AuthUser);
            } catch {
                this._user.set(null);
            }
        } else {
            this._user.set(null);
        }

        this._isAuthenticated.set(isAuth);
        return isAuth;
    }

    /**
     * Sets the authentication state to authenticated.
     *
     * @param userTraits - User traits/info to store
     * @returns {void}
     *
     * @remarks
     * Should be called after successful login or auth validation.
     */
    public setAuthenticated(userTraits: AuthUser): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        const serialized = JSON.stringify(userTraits);
        sessionStorage.setItem('ntv_user_traits', serialized);
        localStorage.setItem('ntv_user_traits', serialized);
        this._userTraits.set(userTraits);
        this._user.set(userTraits);
        this._isAuthenticated.set(true);
    }

    /**
     * Clears authentication state and removes stored credentials.
     *
     * @returns {void}
     *
     * @remarks
     * Should be called on logout or session expiration.
     */
    public clearAuthentication(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        sessionStorage.removeItem('ntv_user_traits');
        this._user.set(null);
        localStorage.removeItem('ntv_user_traits');
        this._userTraits.set(null);
        this._isAuthenticated.set(false);
    }

    /**
     * Retrieves stored user traits from successful authentication.
     *
     * @returns The parsed user traits or null if not available
     *
     * @remarks
     * Returns user info and traits returned from the auth API.
     * This is safe to expose since it doesn't contain the secure session cookie.
     */
    public getUserTraits<T = AuthUser>(): T | null {
        if (!isPlatformBrowser(this.platformId)) {
            return null;
        }
        const data =
            sessionStorage.getItem('ntv_user_traits') ?? localStorage.getItem('ntv_user_traits');
        if (!data) {
            return null;
        }
        try {
            return JSON.parse(data) as T;
        } catch {
            return null;
        }
    }

    /**
     * Clears the server session cookie via the BFF and wipes local auth state,
     * **without** triggering the Auth0 SSO logout redirect.
     *
     * @returns Observable emitting true when the BFF call succeeds, false otherwise.
     *
     * @remarks
     * Used when reaching `/login` or `/auth` on an existing session via an Auth0
     * redirect intended for a different user (e.g. admin opens a dealer's reset
     * link). A full {@link logout} would bounce the page through Auth0 and lose
     * the redirect context — this preserves it while still ending the prior session.
     */
    public serverLogout(): Observable<boolean> {
        if (!isPlatformBrowser(this.platformId)) {
            return of(false);
        }
        return this.http.post('/api/auth/logout', {}, { withCredentials: true }).pipe(
            tap(() => this.clearAuthentication()),
            map(() => true),
            catchError(() => {
                this.clearAuthentication();
                return of(false);
            }),
        );
    }

    /**
     * Logs the user out by clearing server-side cookies, local state, and the Auth0 SSO session.
     *
     * @returns Observable that emits true when the BFF call succeeds, false otherwise
     *
     * @remarks
     * Calls `POST /api/auth/logout` to clear the httpOnly session cookie,
     * wipes `ntv_user_traits` from sessionStorage, and redirects through Auth0
     * so the SSO session is also terminated (otherwise the next login would silently re-auth).
     * Local state is cleared even if the backend call fails.
     */
    public logout(): Observable<boolean> {
        if (!isPlatformBrowser(this.platformId)) {
            return of(false);
        }

        const finalizeAuth0Logout = () => {
            if (this.auth0) {
                this.auth0
                    .logout({ logoutParams: { returnTo: window.location.origin + '/login' } })
                    .subscribe();
            }
        };

        return this.http.post('/api/auth/logout', {}, { withCredentials: true }).pipe(
            tap(() => this.clearAuthentication()),
            map(() => {
                finalizeAuth0Logout();
                return true;
            }),
            catchError(() => {
                this.clearAuthentication();
                finalizeAuth0Logout();
                return of(false);
            }),
        );
    }

    /**
     * Resends the email verification link to the currently authenticated user.
     *
     * @returns Observable emitting the response data, or null on failure
     */
    public resendEmailVerification<T = string>(): Observable<T | null> {
        return this.http
            .post<T>('/api/auth/resend-email-verification', {}, { withCredentials: true })
            .pipe(catchError(() => of(null)));
    }

    /**
     * Retrieves the email verification cooldown expiry for the current user.
     *
     * @returns Observable emitting the expiry data, or null on failure
     */
    public getEmailVerificationExpiry<T = string>(): Observable<T | null> {
        return this.http
            .get<T>('/api/auth/email-verification-expiry', { withCredentials: true })
            .pipe(catchError(() => of(null)));
    }

    /**
     * Retrieves the change password cooldown expiry for the current user.
     *
     * @returns Observable emitting the expiry data, or null on failure
     */
    public getChangePasswordExpiry<T = string>(): Observable<T | null> {
        return this.http
            .get<T>('/api/auth/change-password-expiry', { withCredentials: true })
            .pipe(catchError(() => of(null)));
    }

    /**
     * @description Loads change-password cooldown from `getChangePasswordExpiry` and maps the payload to a single numeric TTL (seconds). Uses `0` when absent or null.
     * @returns Observable emitting `{ ttl: number }` derived from `expiry?.data?.ttl`
     */
    public getChangePasswordCooldownTtl(): Observable<{ ttl: number }> {
        return this.getChangePasswordExpiry<{ data?: { ttl?: number } } | null>().pipe(
            map((expiry) => ({ ttl: expiry?.data?.ttl ?? 0 })),
        );
    }

    /**
     * Checks whether the browser has a non-empty `__Host-session_id` cookie.
     *
     * @returns Observable emitting `{ hasSession: boolean }`. Falls back to `{ hasSession: false }` on error.
     *
     * @remarks
     * The cookie is HttpOnly so it cannot be read from `document.cookie`. The BFF
     * route at `/api/auth/session-status` inspects the incoming `Cookie` header and
     * returns presence to the client without forwarding to the backend API.
     */
    public getSessionStatus(): Observable<{ hasSession: boolean }> {
        return this.http
            .get<{ hasSession: boolean }>('/api/auth/session-status', { withCredentials: true })
            .pipe(
                map((res) => ({ hasSession: !!res?.hasSession })),
                // Fail-open: if BFF endpoint is not deployed, assume session present so the
                // verification-link-expired flow renders rather than the session-expired alert.
                catchError(() => of({ hasSession: true })),
            );
    }

    /**
     * Initiates the change-password email flow for the **current session** user.
     *
     * @returns Observable emitting the response data, or null on failure
     *
     * @remarks
     * `POST /api/auth/change-password` with JSON body `{}` and `withCredentials: true`.
     * For emailing another user (admin dealer reset), use
     * {@link initiatePasswordResetEmailForUserWithCooldown} instead (`{ userId }` body).
     * The BFF resolves the user from the httpOnly session cookie (same contract as the
     * Profile Settings “change password” action).
     */
    public changePassword<T = unknown>(): Observable<T | null> {
        return this.http
            .post<T>('/api/auth/change-password', {}, { withCredentials: true })
            .pipe(catchError(() => of(null)));
    }

    /**
     * Runs {@link changePassword} then reads `/api/auth/change-password-expiry` for the session throttle.
     *
     * @remarks
     * Matches Profile Settings: always requests expiry after `changePassword` completes,
     * even when that call returns null (network/HTTP error path).
     *
     * @returns Observable emitting `{ ttl }` seconds until another change-password request is allowed
     */
    public initiateChangePasswordEmailWithCooldown(): Observable<{ ttl: number }> {
        return this.changePassword().pipe(switchMap(() => this.getChangePasswordCooldownTtl()));
    }

    /**
     * Sends the change-password email to another user (e.g. a dealer) by their Auth user id.
     *
     * @param userId - Target user's id (UUID)
     * @returns Observable emitting `{ ttl }` seconds until another change-password request is allowed
     */
    public initiatePasswordResetEmailForUserWithCooldown(
        userId: string,
    ): Observable<{ ttl: number }> {
        return this.http
            .post<unknown>('/api/auth/change-password', { userId }, { withCredentials: true })
            .pipe(switchMap(() => this.getChangePasswordCooldownTtl()));
    }

    /**
     * Refreshes the access token using the existing session cookie.
     *
     * @returns Observable emitting the response data, or null on failure
     *
     * @remarks
     * Calls `POST /api/auth/refresh` to rotate the access token without
     * requiring a full re-authentication. Relies on the httpOnly session cookie.
     */
    public refreshAccessToken<T = unknown>(): Observable<T | null> {
        return this.http
            .post<T>('/api/auth/refresh', {}, { withCredentials: true })
            .pipe(catchError(() => of(null)));
    }

    /**
     * Fetches the current authenticated user from the backend.
     *
     * @returns Observable emitting the current user data, or null on failure
     */
    public getCurrentUser<T = unknown>(): Observable<T | null> {
        return this.http
            .get<T>('/api/auth/me', { withCredentials: true })
            .pipe(catchError(() => of(null)));
    }

    /**
     * Verifies authentication status with the backend.
     *
     * @returns Observable that emits true if authenticated, false otherwise
     *
     * @remarks
     * This checks the httpOnly session cookie by making a request to the backend.
     * If successful, it syncs the sessionStorage flag for faster subsequent checks.
     */
    public verifyAuthWithBackend(): Observable<boolean> {
        return this.http.post('/api/auth/validate', {}, { withCredentials: true }).pipe(
            map((response: any) => {
                // Backend returns {message: 'Session validated', ...userAuthDetails}
                const isAuth = !!response && response.message === 'Session validated';
                if (isAuth && isPlatformBrowser(this.platformId)) {
                    const serialized = JSON.stringify(response);
                    sessionStorage.setItem('ntv_user_traits', serialized);
                    localStorage.setItem('ntv_user_traits', serialized);
                    this._userTraits.set(response);
                    this._user.set(response as AuthUser);
                } else {
                    this._user.set(null);
                }
                this._isAuthenticated.set(isAuth);
                return isAuth;
            }),
            catchError(() => {
                // Auth verification failed - clear local state
                this.clearAuthentication();
                return of(false);
            }),
        );
    }
}
